/**
 * One tutoring session, from the child's side.
 *
 * The transport is `core-api` §6: POST up, SSE down, resume by cursor. What
 * this hook adds is the honesty layer of SPEC §8.3 — a message the child has
 * typed is on screen immediately, marked *en attente d'envoi* until the server
 * has acknowledged it, and never marked corrected while it sits in the queue.
 *
 * Every submission goes through the outbox, online or not. There is one path,
 * and it is the one that has been exercised offline.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { call } from "@/api/client";
import { ApiError } from "@/api/errors";
import { SessionStream, type SessionEvent } from "@/api/session-stream";
import type { SittingReportOut } from "@/api/schema";
import { readStatementImages, type StatementImage } from "@/ui/EnonceImage";
import {
  drain,
  enqueue,
  list,
  newClientId,
  repoint,
  retry,
  subscribe,
  type OutboxItem,
} from "@/offline/outbox";

const SESSION_KEY = "tuteur.session";

export type ChatEntry = {
  key: string;
  author: "child" | "tutor";
  text: string;
  /** Queued, sent, refused, or still being written by the tutor. */
  state: "queued" | "sent" | "failed" | "writing";
  /** Present on a child's own message: what `retry` needs to send it again. */
  clientId?: string;
};

export type ServedExercise = {
  eventId: number;
  statement: string;
  answer: Record<string, unknown>;
  hints: string[];
  /**
   * L'énoncé sous forme d'image, quand l'exercice vient d'un manuel
   * (`exercise` §19 bis). Absent des exercices générés, et absent tant que le
   * back-end n'émet pas `statement_image` — voir `readStatementImage`.
   */
  image: StatementImage | null;
  /**
   * Toutes les pages de l'énoncé, `image` comprise, quand il a été découpé en
   * plusieurs rectangles. Facultatif : absent, on n'affiche que `image`.
   */
  images?: StatementImage[];
  /**
   * Le titre de l'ouvrage dont l'énoncé est découpé, quand il y en a un
   * (`schemas.nom_du_parascolaire`). Il accompagne l'image et jamais la
   * transcription : `null` sur tout exercice généré.
   */
  parascolaire: string | null;
};

export type SessionState = {
  ready: boolean;
  status: "connecting" | "live" | "lost";
  /** Le compte rendu de fin, en lecture seule, une fois la séance close. */
  report: SittingReportOut | null;
  finishing: boolean;
  finish: () => Promise<void>;
  /** Le tuteur a le message et n'a pas encore commencé à répondre. */
  thinking: boolean;
  entries: ChatEntry[];
  exercise: ServedExercise | null;
  hints: string[];
  corrections: SessionEvent[];
  queued: OutboxItem[];
  /** Renvoyer un message que le serveur avait refusé. */
  retry: (clientId: string) => Promise<void>;
  send: (text: string) => Promise<void>;
  error: string | null;
};

/** Un refus « cette séance n'existe pas » : l'identifiant stocké est périmé. */
function isStaleSession(item: OutboxItem): boolean {
  return (
    item.kind === "session.message" &&
    item.state === "failed" &&
    (item.lastError ?? "").toLowerCase().includes("séance")
  );
}

async function sendItem(item: OutboxItem): Promise<void> {
  if (!item.tutorSessionId) return;
  const text = item.math
    ? `${item.text}\n\n[réponse] ${item.math.latex}${item.math.mathjson ? `\n[mathjson] ${item.math.mathjson}` : ""}`
    : item.text;
  await call("postMessage", {
    params: { tutor_session_id: item.tutorSessionId },
    body: { text, client_id: item.clientId, duration_ms: item.durationMs ?? null },
  });
}

export function useTutorSession(): SessionState {
  const [tutorSessionId, setTutorSessionId] = useState<string | null>(
    () => sessionStorage.getItem(SESSION_KEY),
  );
  const [events, setEvents] = useState<SessionEvent[]>([]);
  const [fragment, setFragment] = useState("");
  const [status, setStatus] = useState<"connecting" | "live" | "lost">("connecting");
  const [queued, setQueued] = useState<OutboxItem[]>([]);
  /**
   * Le tuteur a reçu le message mais n'a pas encore écrit un mot.
   *
   * Sans ce témoin, l'écran ne montre rien entre l'envoi et le premier fragment
   * — et sur une tablette, un écran qui ne bouge pas se lit comme une panne, pas
   * comme une réflexion.
   */
  const [thinking, setThinking] = useState(false);
  /**
   * Le compte rendu de fin de séance, une fois « Finir » pressé.
   *
   * L'enfant le lit et ne l'écrit pas : c'est le regard du tuteur sur le
   * travail, pas le sien. Elle le voit quand même — rien ne part chez ses
   * parents qu'elle n'ait pu lire, et c'est ce qui rend la règle tenable.
   */
  const [report, setReport] = useState<SittingReportOut | null>(null);
  const [finishing, setFinishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const streamRef = useRef<SessionStream | null>(null);

  // --- Open (or recover) the session ---------------------------------------
  useEffect(() => {
    if (tutorSessionId) return;
    let cancelled = false;
    void (async () => {
      try {
        const opened = await call("openSession");
        if (cancelled) return;
        sessionStorage.setItem(SESSION_KEY, opened.tutor_session_id);
        setTutorSessionId(opened.tutor_session_id);
      } catch {
        // Offline at launch: the session opens when the network returns. The
        // child sees the network banner, not a stack trace.
        setError("Je n'arrive pas à démarrer la séance pour le moment.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tutorSessionId]);

  // --- Stream ---------------------------------------------------------------
  useEffect(() => {
    if (!tutorSessionId) return;
    const stream = new SessionStream(tutorSessionId, {
      onEvent: (event) => {
        setFragment("");
        if (event.type === "tutor.message") setThinking(false);
        setEvents((previous) =>
          previous.some((seen) => seen.id === event.id) ? previous : [...previous, event],
        );
      },
      onFragment: (text) => {
        setThinking(false);
        setFragment(text);
      },
      onStatus: setStatus,
    });
    streamRef.current = stream;
    stream.open();
    return () => {
      stream.close();
      streamRef.current = null;
    };
  }, [tutorSessionId]);

  // --- Outbox ---------------------------------------------------------------
  const refreshQueue = useCallback(() => {
    void list().then(setQueued);
  }, []);

  useEffect(() => {
    refreshQueue();
    const unsubscribe = subscribe(refreshQueue);
    const flush = () => {
      void drain(sendItem).then(refreshQueue);
      streamRef.current?.resume();
    };
    window.addEventListener("online", flush);
    const timer = window.setInterval(() => void drain(sendItem).then(refreshQueue), 20_000);
    flush();
    return () => {
      unsubscribe();
      window.removeEventListener("online", flush);
      window.clearInterval(timer);
    };
  }, [refreshQueue]);

  /**
   * La séance stockée n'existe plus côté serveur (404).
   *
   * Arrive dès qu'un navigateur change d'enfant ou que la base est réinitialisée :
   * l'identifiant survit dans `sessionStorage` et chaque message est refusé, pour
   * toujours. On en ouvre une neuve et on ré-adresse ce qui attendait — le texte
   * de l'enfant appartient à l'enfant, pas à l'identifiant sous lequel il a été
   * écrit.
   */
  const recoverSession = useCallback(async () => {
    const opened = await call("openSession");
    sessionStorage.setItem(SESSION_KEY, opened.tutor_session_id);
    setTutorSessionId(opened.tutor_session_id);
    setEvents([]);
    await repoint(opened.tutor_session_id);
    await drain(sendItem);
    refreshQueue();
  }, [refreshQueue]);

  const retrySend = useCallback(
    async (clientId: string) => {
      await retry(clientId);
      await drain(sendItem);
      refreshQueue();
    },
    [refreshQueue],
  );

  const finish = useCallback(async () => {
    if (!tutorSessionId || finishing) return;
    setFinishing(true);
    try {
      setReport(await call("endSession", { params: { tutor_session_id: tutorSessionId } }));
      // La séance est close : le prochain message en ouvrira une neuve.
      sessionStorage.removeItem(SESSION_KEY);
    } catch (failure) {
      setError(
        failure instanceof ApiError
          ? failure.message
          : "Je n'arrive pas à finir la séance pour le moment.",
      );
    } finally {
      setFinishing(false);
    }
  }, [tutorSessionId, finishing]);

  const send = useCallback(
    async (text: string) => {
      if (!tutorSessionId || !text.trim()) return;
      await enqueue({
        clientId: newClientId(),
        kind: "session.message",
        tutorSessionId,
        text: text.trim(),
        math: null,
      });
      setThinking(true);
      await drain(sendItem);
      refreshQueue();

      // Un message qui n'est pas parti n'attend aucune réponse.
      if ((await list()).some((item) => item.state === "failed")) setThinking(false);

      // Séance morte sous le message : la remplacer et renvoyer, sans rien
      // demander à l'enfant.
      if ((await list()).some(isStaleSession)) {
        try {
          await recoverSession();
        } catch {
          setError("Je n'arrive pas à rouvrir la séance. Ton message est gardé.");
        }
      }
    },
    [tutorSessionId, refreshQueue, recoverSession],
  );

  // --- Projection -----------------------------------------------------------
  const entries = useMemo<ChatEntry[]>(() => {
    const fromServer: ChatEntry[] = events
      .filter((event) => event.type === "child.message" || event.type === "tutor.message")
      .map((event) => ({
        key: `e${event.id}`,
        author: event.type === "child.message" ? "child" : "tutor",
        text: typeof event.payload.text === "string" ? event.payload.text : "",
        state: "sent" as const,
      }));

    // Volontairement non filtré sur la séance courante : un message refusé
    // parce que sa séance a disparu doit rester visible, sinon l'enfant voit
    // son texte s'évaporer — exactement ce qu'on cherche à éviter.
    const pending: ChatEntry[] = queued
      .filter((item) => item.kind === "session.message")
      .map((item) => ({
        key: `q${item.clientId}`,
        author: "child" as const,
        text: item.text,
        state: item.state === "failed" ? ("failed" as const) : ("queued" as const),
        clientId: item.clientId,
      }));

    const writing: ChatEntry[] = fragment
      ? [{ key: "fragment", author: "tutor", text: fragment, state: "writing" }]
      : [];

    return [...fromServer, ...pending, ...writing];
  }, [events, queued, fragment, tutorSessionId]);

  const exercise = useMemo<ServedExercise | null>(() => {
    const served = [...events].reverse().find((event) => event.type === "exercise.served");
    if (!served) return null;
    const payload = served.payload;
    return {
      eventId: served.id,
      statement: typeof payload.statement === "string" ? payload.statement : "",
      answer: (payload.answer as Record<string, unknown>) ?? { kind: "symbolic" },
      hints: Array.isArray(payload.hints) ? (payload.hints as string[]) : [],
      image: readStatementImages(payload)[0] ?? null,
      images: readStatementImages(payload),
      parascolaire: typeof payload.parascolaire === "string" ? payload.parascolaire : null,
    };
  }, [events]);

  const hints = useMemo(
    () =>
      events
        .filter((event) => event.type === "hint.delivered")
        .map((event) => (typeof event.payload.text === "string" ? event.payload.text : ""))
        .filter(Boolean),
    [events],
  );

  const corrections = useMemo(() => events.filter((event) => event.type === "correction"), [events]);

  return {
    ready: Boolean(tutorSessionId),
    status,
    thinking,
    report,
    finishing,
    finish,
    entries,
    exercise,
    hints,
    corrections,
    queued,
    send,
    retry: retrySend,
    error,
  };
}

/** Submitting an answer, a report or an absence — same queue, same idempotence. */
export async function queueSubmission(
  tutorSessionId: string | null,
  kind: OutboxItem["kind"],
  text: string,
  math: OutboxItem["math"] = null,
  /**
   * Le temps passé sur l'exercice, quand l'appelant l'a mesuré.
   *
   * Il entre dans la file avec le message plutôt que d'être relu à l'envoi :
   * un message écrit hors ligne et transmis le lendemain doit porter la durée
   * de la veille, pas celle du trajet réseau.
   */
  durationMs?: number,
): Promise<void> {
  await enqueue({ clientId: newClientId(), kind, tutorSessionId, text, math, durationMs });
  await drain(sendItem);
}
